import type { Channel } from 'pusher-js';
import type { PropsWithChildren } from 'react';
import { createContext, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';

import pusherClient from '~/config/initPusher';
import { logWarn } from '~/common/utils/log';

type PusherContextValue = {
  subscribeToChannel: (
    channelId: string,
    events: { name: string; callback: (data: unknown) => void }[]
  ) => void;
  unsubscribeFromChannel: (
    channelId: string,
    events: { name: string; callback: (data: unknown) => void }[]
  ) => void;
  channels: Channel[];
  clientId: string;
  isConnected: boolean;
  hasError: boolean;
  error: Error | null;
};

const PusherContext = createContext<PusherContextValue>({
  subscribeToChannel: () => {},
  unsubscribeFromChannel: () => {},
  channels: [],
  clientId: '',
  isConnected: false,
  hasError: false,
  error: null,
});

type PusherContextProviderProps = PropsWithChildren<{
  enabled?: boolean;
}>;

function PusherContextProvider({ children, enabled = true }: PusherContextProviderProps) {
  // A ref, not state: the deferred unsubscribe must see callbacks bound after its render.
  const channelCallbackCountRef = useRef<Record<string, number>>({});
  const [unbindVersion, setUnbindVersion] = useState(0);
  const [clientId] = useState<string>(uuidv4());
  const [isConnected, setIsConnected] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    if (enabled && pusherClient) {
      // Check if already connected on mount
      if (pusherClient.connection.state === 'connected') {
        setIsConnected(true);
        setHasError(false);
        setError(null);
      }

      pusherClient.connection.bind(
        'state_change',
        (states: { previous: string; current: string }) => {
          if (states.current === 'connected') {
            setIsConnected(true);
            setHasError(false);
            setError(null);
          } else if (states.current === 'failed') {
            setIsConnected(false);
            setHasError(true);
            setError(new Error(`Pusher connection failed`));
          } else if (states.current === 'disconnected') {
            setIsConnected(false);
            setHasError(false);
            setError(null);
          }
        }
      );

      pusherClient.connection.bind('error', (e: string) => {
        logWarn('pusher:connection_error', e);
        setIsConnected(false);
        setHasError(true);
        setError(new Error(`Pusher connection error: ${e}`));
      });

      // keep a count of how many times we are trying to re-try
      if (
        pusherClient.connection.state === 'disconnected' ||
        pusherClient.connection.state === 'failed'
      ) {
        pusherClient.connect();
      }

      return () => {
        pusherClient.connection.unbind('state_change');
        pusherClient.connection.unbind('error');
      };
    }

    if (enabled) {
      logWarn('PusherContext: pusherClient is null/undefined');
      setHasError(true);
      setError(new Error('Pusher client not initialized'));
    }
  }, [enabled]);

  useEffect(() => {
    const channelCallbackCount = channelCallbackCountRef.current;
    const unsubscribeChannels = Object.keys(channelCallbackCount).filter(
      (channelId) => channelCallbackCount[channelId] === 0
    );
    unsubscribeChannels.forEach((channelId) => {
      pusherClient.unsubscribe(channelId);
    });
  }, [unbindVersion]);

  const subscribeToChannel = (
    channelId: string,
    events: { name: string; callback: (data: unknown) => void }[]
  ) => {
    if (pusherClient && !hasError) {
      try {
        // Always subscribe: it reuses a live channel and reinstates a cancelled pending one.
        const channel = pusherClient.subscribe(channelId);

        events.forEach((event) => {
          channel.bind(event.name, event.callback);
          channelCallbackCountRef.current[channelId] =
            (channelCallbackCountRef.current[channelId] || 0) + 1;
        });
      } catch (err) {
        logWarn('PusherContext: Failed to subscribe to channel', {
          channelId,
          error: err,
        });
        setHasError(true);
        setError(err instanceof Error ? err : new Error('Failed to subscribe to channel'));
      }
    }
  };

  const unsubscribeFromChannel = (
    channelId: string,
    events: { name: string; callback: (data: unknown) => void }[]
  ) => {
    const channel = pusherClient.channels.find(channelId);
    events.forEach((event) => {
      channel?.unbind(event.name, event.callback);
      channelCallbackCountRef.current[channelId] -= 1;
    });
    setUnbindVersion((version) => version + 1);
  };

  const value = useMemo(
    () =>
      ({
        subscribeToChannel,
        unsubscribeFromChannel,
        channels: pusherClient.channels.all(),
        clientId,
        isConnected,
        hasError,
        error,
      }) satisfies PusherContextValue,
    [isConnected, hasError, error]
  );

  return <PusherContext.Provider value={value}>{children}</PusherContext.Provider>;
}

export { PusherContext, PusherContextProvider };
